import React, { forwardRef, ReactElement } from 'react';
import { useMergeRefs } from '@floating-ui/react';

import { Comp } from '@redsift/design-system';
import { useToggletipContext } from '../toggletip';
import { ToggletipTriggerProps } from './types';
import classNames from 'classnames';
import { StyledToggletipTrigger } from './styles';

const COMPONENT_NAME = 'ToggletipTrigger';
const CLASSNAME = 'redsift-toggletip-trigger';

/**
 * The ToggletipTrigger component.
 */
export const ToggletipTrigger: Comp<ToggletipTriggerProps, HTMLButtonElement> = forwardRef((props, ref) => {
  const { children } = props;

  const { getReferenceProps, isOpen, handleOpen, refs, toggletipId, triggerClassName, color } = useToggletipContext();
  const childrenRef = (children as any).ref;
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);

  if (React.isValidElement(children)) {
    return (
      <StyledToggletipTrigger
        {...getReferenceProps({
          ref: triggerRef,
          ...props,
          'aria-describedby': toggletipId,
        })}
        type="button"
        aria-expanded={isOpen}
        onClick={undefined}
        onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space') {
            event.preventDefault();
            if (props.onClick) {
              props.onClick(event as never);
            } else {
              handleOpen(!isOpen);
            }
          }
        }}
        className={classNames(triggerClassName)}
      >
        {React.cloneElement(children as ReactElement, {
          ...children.props,
          isActive: isOpen,
          color: color ?? children.props.color,
        })}
      </StyledToggletipTrigger>
    );
  }

  return null;
});
ToggletipTrigger.className = CLASSNAME;
ToggletipTrigger.displayName = COMPONENT_NAME;
